import api from "../api";
import { Attachment } from "../attachments/attachmentsModel";
import { QboCustomerRecord } from "../../domain/quickbooks/quickbooksModel";

export type Customer = {
  id: number
  url: string
  displayName: string
}

export const getCustomer = async (customerId: number): Promise<Customer> => {
  return (await api.get(`/customers/${customerId}`)).data
}

export type CustomerDto = Customer & {
 contact: string,
 tags: string,
 isCustomer: boolean,
}

export const getCustomers = async (search: string, pageSize: number, page: number, isLead: boolean, isCustomer: boolean, showDeleted: boolean): Promise<CustomerDto[]> => {
  const url = showDeleted ? '/customers/deleted' : '/customers'
  return (await api.get<{ items: CustomerDto[] }>(url, {
    params: {
      search,
      isCustomer,
      isLead,
      page,
      limit: pageSize,
    }
  })).data.items
}

// Full customer detail — the shape returned by GET /customers/{id} (CustomerDTO in the api repo).
export type CustomerLocation = {
  latitude: number
  longitude: number
}

export type CustomerPhone = {
  id: number
  customerId: number
  type: number
  number: string
  description?: string | null
  isPrimary: boolean
  removed?: boolean
}

export type CustomerEmail = {
  id: number
  customerId: number
  address: string
  description?: string | null
  isPrimary: boolean
  removed?: boolean
}

export type CustomerPropertyListItem = {
  id: number
  address1: string
  mapPosition: string
}

export type CustomerTag = {
  text: string
}

export type Consult = {
  id: number
  customerId: number
  projectId?: number | null
  calendarEventId?: number | null
  projectName?: string | null
  projectSold?: boolean
  organizerEmail?: string | null
  bestTimeToCall?: string | null
  fee?: string | null
  consultationDate?: string | null
  summary?: string | null
  description?: string | null
  notes?: string | null
  timeFrame?: string | null
  salesLead?: string | null
  status: number
  updated?: string
  updatedUtc?: string
  removed?: boolean
  emailBody?: string | null
  cannedResponse?: string | null
  createdBy?: string | null
}

export type CustomerDetail = {
  id: number
  url: string
  displayName: string
  company?: string | null
  firstName?: string | null
  lastName?: string | null
  address1?: string | null
  address2?: string | null
  city?: string | null
  county?: string | null
  state?: string | null
  countryCode?: string | null
  zip?: string | null
  location?: CustomerLocation | null
  firstName2?: string | null
  lastName2?: string | null
  displayAs: number
  salesLead?: string | null
  salesLeadNotes?: string | null
  isActive: boolean
  isCustomer: boolean
  tags?: string | null
  primaryPhotoId?: string | null
  notes?: string | null
  qboId?: string | null
  qboDisplayName?: string | null
  propertyNickname?: string | null
  leadSince?: string | null
  customerSince?: string | null
  customLeadSince?: string | null
  customCustomerSince?: string | null
  emails: CustomerEmail[]
  phones: CustomerPhone[]
  primaryPhotoUrl?: string | null
  properties: number
  propertyList?: CustomerPropertyListItem[] | null
  attachments: number
  projects: number
  consults?: Consult[] | null
  tagsArray?: CustomerTag[]
}

// Client-side working shapes carry a few extra fields the UI derives (never read back from the server).
export type CustomerSaveRequest = Partial<CustomerDetail> & { id: number }
export type ConsultSaveRequest = Partial<Consult> & {
  selectedSalesLeads?: Record<string, boolean>
}

export const getCustomerDetail = async (customerId: number, showDeleted = false): Promise<CustomerDetail> => {
  const url = showDeleted ? `/customers/deleted/${customerId}` : `/customers/${customerId}`
  return (await api.get<CustomerDetail>(url)).data
}

export const saveCustomer = async (customer: CustomerSaveRequest, createFirstPropertyAutomatically = false): Promise<CustomerDetail> => {
  const url = createFirstPropertyAutomatically ? '/customers?createFirstPropertyAutomatically=true' : '/customers'
  return (await api.post<CustomerDetail>(url, customer)).data
}

export const deleteCustomer = async (customerId: number): Promise<void> => {
  await api.delete(`/customers/${customerId}`)
}

export const restoreCustomer = async (customerId: number): Promise<void> => {
  await api.post(`/customers/restore/${customerId}`)
}

export const getCustomerTags = async (search: string): Promise<string[]> => {
  return (await api.get<string[]>('/customers/tags', { params: { search } })).data
}

export const getCustomerPhotos = async (customerId: number): Promise<Attachment[]> => {
  return (await api.get<Attachment[]>(`/attachments/photos/${customerId}`)).data
}

export type CustomerPropertyRow = {
  id: number
  url: string
  customerId: number
  address: string
  cityStateZip: string
  tags: string
  isActive: boolean
  nicknameAndAddress: string
  nickname: string
}

export const getCustomerProperties = async (customerId: number): Promise<CustomerPropertyRow[]> => {
  return (await api.get<CustomerPropertyRow[]>(`/customers/${customerId}/properties`)).data
}

export type CustomerInvoiceRow = {
  createDate: string
  dueDate?: string | null
  amount: number
  projectId?: number | null
  project: { id: number; name: string }
}

export const getCustomerInvoices = async (customerId: number): Promise<CustomerInvoiceRow[]> => {
  return (await api.get<CustomerInvoiceRow[]>(`/invoices/customer/${customerId}`)).data
}

export type SchedulableProject = {
  id: number
  name: string
  no: string
}

export type SchedulableProjects = {
  unscheduled: SchedulableProject[]
  scheduled: SchedulableProject[]
}

export const getConsultationProjects = async (customerId: number): Promise<SchedulableProjects> => {
  return (await api.get<SchedulableProjects>(`/customers/${customerId}/consultation-projects`)).data
}

export const addEditConsultation = async (consult: ConsultSaveRequest): Promise<Consult> => {
  return (await api.post<Consult>('/consultations/addEditConsultation', consult)).data
}

export type EmailAddresses = {
  addresses: string[]
  replyToAddresses: string[]
}

export const sendConsultationInvites = async (customerId: number, consultId: number, emailAddresses: EmailAddresses): Promise<void> => {
  await api.post(`/customers/${customerId}/sendconsultationinvites/${consultId}`, emailAddresses)
}

export type SalesUser = {
  displayName: string
  email: string
  color: string
  avatar: string
  hasCustomAvatar: boolean
  selected: boolean
  userId: string
}

export const getSalesUsers = async (): Promise<SalesUser[]> => {
  return (await api.get<SalesUser[]>('/users/sales')).data
}

export const getSalesLeads = async (): Promise<string[]> => {
  return (await api.get<string[]>('/salesleads')).data
}

// Returns the API's summary, e.g. "Successfully imported 2 customer(s). "
export const importQboCustomers = async (customers: QboCustomerRecord[]): Promise<string> => {
  return (await api.post<string>('/customers/qbo/import', customers)).data
}

export const EmailTemplateTypes = {
  Consultation: 0,
  Proposal: 1,
} as const

export type EmailTemplate = {
  id: number
  guid: string
  tenantId: number
  name: string
  eventDescription?: string | null
  emailBody?: string | null
  emailSubject?: string | null
  type: number
}

export const getCannedResponses = async (): Promise<EmailTemplate[]> => {
  return (await api.get<EmailTemplate[]>('/cannedresponses')).data
}

export const filterConsultationCannedResponses = (templates: EmailTemplate[]): EmailTemplate[] => {
  return templates.filter(t => t.type === EmailTemplateTypes.Consultation)
}

// The Angular "create new" flow posts to /{type}/create where type is one of these route segments.
export const CreateJobTypes = {
  project: 'projects',
  estimate: 'estimates',
  serviceTicket: 'tickets',
  maintenanceContract: 'maintenance',
} as const
export type CreateJobType = (typeof CreateJobTypes)[keyof typeof CreateJobTypes]

export type CreateJobRequest = {
  customerId: number
  propertyId?: number
  timecardInternalNotesInputMode?: number
  timecardClientNotesInputMode?: number
  name: string
}

export type CreatedJob = {
  id: number
  url: string
}

export const createJob = async (type: CreateJobType, request: CreateJobRequest): Promise<CreatedJob> => {
  return (await api.post<CreatedJob>(`/${type}/create`, request)).data
}

export const downloadConsultationIcs = async (consultId: number): Promise<Blob> => {
  return (await api.get(`/calendar/consultations/${consultId}`, { responseType: 'blob' })).data
}